import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

export default function Navbar() {
  const { t, lang, pathFor, toggleLang } = useLanguage();
  const { hash, pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: t.nav.home, href: pathFor('/'), match: 'home' },
    { label: t.nav.features, href: pathFor('/#features'), match: 'features' },
    { label: t.nav.pricing, href: pathFor('/#pricing'), match: 'pricing' },
  ];

  const isActive = (match) => {
    if (match === 'home') return pathname === pathFor('/') && !hash;
    return hash === `#${match}`;
  };

  return (
    <nav
      className={`sticky top-0 z-50 transition-[background-color,backdrop-filter,border-color,box-shadow] duration-300 ${
        scrolled
          ? 'bg-white/80 supports-[backdrop-filter]:backdrop-blur-md border-b border-ct-line ct-shadow-sm'
          : 'bg-white border-b border-ct-line'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link
            to={pathFor('/')}
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <span
              aria-hidden="true"
              className="w-7 h-7 flex items-center justify-center bg-ct-brand text-white font-black text-sm ct-shadow-sm"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              C
            </span>
            <span
              className="font-black text-xl text-ct-ink tracking-tight"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              CarniTrack
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {links.map((l) => {
              const active = isActive(l.match);
              return (
                <a
                  key={l.href}
                  href={l.href}
                  className={`relative px-3 py-2 text-sm font-medium transition-colors duration-200 cursor-pointer ${
                    active ? 'text-ct-ink' : 'text-ct-ink-3 hover:text-ct-ink'
                  }`}
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {l.label}
                  <span
                    aria-hidden="true"
                    className={`absolute left-3 right-3 -bottom-[1px] h-0.5 bg-ct-brand origin-left transition-transform duration-300 ${
                      active ? 'scale-x-100' : 'scale-x-0'
                    }`}
                  />
                </a>
              );
            })}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="text-[11px] font-bold tracking-widest text-ct-ink-3 hover:text-ct-ink border border-ct-line hover:border-ct-ink-2 px-2.5 py-1 transition-colors duration-200 cursor-pointer"
              aria-label={`Switch language to ${lang === 'EN' ? 'TR' : 'EN'}`}
            >
              {lang === 'EN' ? 'TR' : 'EN'}
            </button>
            <Link
              to={pathFor('/signin')}
              data-testid="nav-signin-link"
              className="text-sm font-medium text-ct-ink-2 hover:text-ct-brand transition-colors duration-200 cursor-pointer"
            >
              {t.nav.signin}
            </Link>
            <Link
              to={pathFor('/setup')}
              data-testid="nav-setup-btn"
              className="group inline-flex items-center gap-1.5 bg-ct-ink text-white text-xs font-bold uppercase tracking-wider px-4 h-9 hover:bg-ct-brand transition-colors duration-200 cursor-pointer"
            >
              {t.nav.setup}
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <button
            className="md:hidden p-2 text-ct-ink-2 hover:text-ct-ink transition-colors cursor-pointer"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-ct-line px-4 py-4 flex flex-col gap-1">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-ct-ink-2 hover:text-ct-ink font-medium text-sm py-2 transition-colors"
            >
              {l.label}
            </a>
          ))}
          <div className="mt-2 pt-4 border-t border-ct-line flex items-center gap-3 flex-wrap">
            <Link
              to={pathFor('/signin')}
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-ct-ink-2"
            >
              {t.nav.signin}
            </Link>
            <Link
              to={pathFor('/setup')}
              onClick={() => setOpen(false)}
              className="bg-ct-ink text-white text-xs font-bold uppercase tracking-wider px-4 py-2 hover:bg-ct-brand transition-colors"
            >
              {t.nav.setup}
            </Link>
            <button
              onClick={() => toggleLang()}
              data-testid="lang-toggle-mobile"
              className="ml-auto text-xs font-bold text-ct-ink-3 border border-ct-line px-2 py-1 hover:text-ct-ink hover:border-ct-ink-2 transition-colors"
            >
              {lang === 'EN' ? 'TR' : 'EN'}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
